/**
 * Utility functions for deriving numeric offsets from anchor times.
 * Used when a backend does not accept anchorTime directly.
 */

/**
 * Returns true when the TSS is an exact divisor of 60 (constant grid),
 * meaning offset input is appropriate. Defaults to true when TSS is invalid.
 */
export function offsetIsConstMode(tssMinutes: number): boolean {
  if (!Number.isFinite(tssMinutes) || tssMinutes <= 0) return true;
  return Math.abs((60 / tssMinutes) - Math.round(60 / tssMinutes)) < 1e-9;
}

/**
 * Formats an ISO datetime string to "YYYY-MM-DD HH:MM:SS" (UTC).
 */
export function toAnchorStr(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}`;
}

/**
 * Computes a numeric offset (in minutes) from an anchor datetime string.
 *
 * The offset is the remainder of (anchorMinutes - timeMinMinutes) modulo tssMinutes,
 * adjusted so the result is in [0, tssMinutes).
 *
 * @param anchorIso  - Anchor time in "YYYY-MM-DD HH:MM:SS" or ISO format (UTC)
 * @param timeMinRawIso - Earliest raw timestamp from the file (ISO or "YYYY-MM-DD HH:MM:SS")
 * @param tssMinutes - Time step size in minutes
 * @returns Derived offset in minutes
 */
export function deriveOffsetFromAnchor(
  anchorIso: string,
  timeMinRawIso: string,
  tssMinutes: number
): number {
  const parseUtcMinutes = (s: string): number => {
    // Handle "YYYY-MM-DD HH:MM:SS" format by replacing space with 'T' and appending 'Z'
    const normalized = s.includes('T') ? s : s.replace(' ', 'T') + (s.endsWith('Z') ? '' : 'Z');
    const d = new Date(normalized);
    if (Number.isNaN(d.getTime())) throw new Error(`Invalid datetime: ${s}`);
    return Math.round(d.getTime() / 60000);
  };

  const anchorMinutes = parseUtcMinutes(anchorIso);
  const timeMinMinutes = parseUtcMinutes(timeMinRawIso);
  const diff = anchorMinutes - timeMinMinutes;
  const mod = ((diff % tssMinutes) + tssMinutes) % tssMinutes;
  return mod;
}
